/* Plain-CSS translation of the React SDK's Tailwind utilities (the embed renders
   in a shadow root with no Tailwind). Values are Tailwind defaults: text-xs =
   12px/16px, gap-1.5 = 6px, rounded-2xl = 16px, black/40 = rgba(0,0,0,.4), and
   so on. Theme variants mirror the React `dark` branches and key off the
   data-moss-theme attribute the bubble stamps on its root. */
import { TEASER_FONT_FAMILY } from "@moss-js/founding-agent/core";

export const CSS = `
:host {
  all: initial;
}
:host * {
  box-sizing: border-box;
  font-family: ui-sans-serif, system-ui, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
}
/* Preflight-style control reset. Bare element selectors keep the specificity
   below the single-attribute rules that style each control. */
button, input {
  font: inherit;
  color: inherit;
  margin: 0;
}
button {
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}
:host [hidden] {
  display: none !important;
}

/* Custom properties. --moss-offset-x / --moss-offset-y are read with 24px
   fallbacks instead of being declared here, so page CSS on :root or on the
   moss-founding-agent element can set them. */
:host {
  --moss-bubble-size: 56px;
  --moss-z-index: 2147483000;
}

/* Positioning wrapper and the React bubble's flex column: [chat panel |
   attribution | teaser | pill] in DOM order, right-aligned. The pill (bubble.ts)
   animates its own width, so this sizes to content. --_moss-avoid-lift is
   set by avoidSelector while a matching element would cover the pill. */
.moss-bubble {
  position: fixed;
  bottom: calc(max(var(--moss-offset-y, 24px), env(safe-area-inset-bottom)) + var(--_moss-avoid-lift, 0px));
  right: max(var(--moss-offset-x, 24px), env(safe-area-inset-right));
  z-index: var(--moss-z-index);
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  width: max-content;
  line-height: 1.5;
  color: #000;
}
:host([data-side="left"]) .moss-bubble {
  right: auto;
  left: max(var(--moss-offset-x, 24px), env(safe-area-inset-left));
  align-items: flex-start;
}
/* Centered: auto side margins center the max-content column between both edges. */
:host([data-side="center"]) .moss-bubble {
  left: 0;
  right: 0;
  margin-inline: auto;
  align-items: center;
}

/* Inline positioning: host flows in-document; bubble goes relative */
:host([data-position="inline"]) { display: inline-block; }
:host([data-position="inline"]) .moss-bubble {
  position: relative;
  bottom: auto;
  right: auto;
  z-index: auto;
  display: inline-flex;
}

/* Passthrough layer: never scrims, never blocks page clicks; each surface inside
   positions itself with position:fixed and re-enables pointer events. */
.moss-overlay {
  position: fixed;
  inset: 0;
  z-index: var(--moss-z-index);
  pointer-events: none;
  line-height: 1.5;
  color: #000;
}

/* Pill / idle circle */
[data-moss-bubble] {
  position: relative;
  height: var(--moss-bubble-size);
  border-radius: 9999px;
  background: #fff;
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  display: flex;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  transition: width 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
[data-moss-theme="dark"] [data-moss-bubble] {
  background: #111214;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.1);
}
[data-moss-avatar] {
  position: relative;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: var(--moss-bubble-size);
  height: var(--moss-bubble-size);
  border-radius: 9999px;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-avatar]:hover { transform: scale(1.04); }
[data-moss-avatar]:disabled { cursor: default; transform: none; }
[data-moss-connecting-ring] {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  pointer-events: none;
  animation: moss-pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
[data-moss-controls] {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 8px 0 2px;
  flex: 1;
  min-width: 0;
}
[data-moss-status] {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.5);
}
[data-moss-theme="dark"] [data-moss-status] { color: rgba(255, 255, 255, 0.6); }
[data-moss-chat-toggle],
[data-moss-mute],
[data-moss-end] {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 9999px;
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-chat-toggle],
[data-moss-mute] { color: rgba(0, 0, 0, 0.6); }
[data-moss-chat-toggle]:hover,
[data-moss-mute]:hover { background: rgba(0, 0, 0, 0.06); }
[data-moss-chat-toggle][data-open] { background: rgba(0, 0, 0, 0.06); color: #000; }
[data-moss-mute][data-muted],
[data-moss-end] { background: #fef2f2; color: #ef4444; }
[data-moss-mute][data-muted]:hover,
[data-moss-end]:hover { background: #fee2e2; }
[data-moss-theme="dark"] [data-moss-chat-toggle],
[data-moss-theme="dark"] [data-moss-mute] { color: rgba(255, 255, 255, 0.7); }
[data-moss-theme="dark"] [data-moss-chat-toggle]:hover,
[data-moss-theme="dark"] [data-moss-mute]:hover { background: rgba(255, 255, 255, 0.1); }
[data-moss-theme="dark"] [data-moss-chat-toggle][data-open] { background: rgba(255, 255, 255, 0.1); color: #fff; }
[data-moss-theme="dark"] [data-moss-mute][data-muted],
[data-moss-theme="dark"] [data-moss-end] { background: rgba(239, 68, 68, 0.15); color: #f87171; }
[data-moss-theme="dark"] [data-moss-mute][data-muted]:hover,
[data-moss-theme="dark"] [data-moss-end]:hover { background: rgba(239, 68, 68, 0.25); }

/* "built on moss" label above the live pill */
[data-moss-attribution] {
  margin: 0 12px 6px 0;
  pointer-events: none;
  user-select: none;
  font-size: 10px;
  line-height: 15px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(0, 0, 0, 0.4);
}
:host([data-side="left"]) [data-moss-attribution] { margin: 0 0 6px 12px; }
:host([data-side="center"]) [data-moss-attribution] { margin: 0 0 6px; }
[data-moss-attribution] span { color: rgba(0, 0, 0, 0.7); }
[data-moss-theme="dark"] [data-moss-attribution] { color: rgba(255, 255, 255, 0.4); }
[data-moss-theme="dark"] [data-moss-attribution] span { color: rgba(255, 255, 255, 0.7); }

/* Teaser: the agent's opening line above the idle pill, aligned by the column.
   A frosted card with a tail toward the orb; it grows out of the orb's side
   after a beat. */
[data-moss-teaser] {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  max-width: min(272px, calc(100vw - 32px));
  margin: 0 0 12px;
  padding: 10px 8px 10px 16px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.92);
  -webkit-backdrop-filter: blur(20px) saturate(1.6);
  backdrop-filter: blur(20px) saturate(1.6);
  color: rgba(15, 20, 16, 0.92);
  box-shadow:
    inset 0 0 0 1px rgba(15, 20, 16, 0.07),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 1px 2px rgba(15, 20, 16, 0.04),
    0 6px 14px -4px rgba(15, 20, 16, 0.08),
    0 22px 44px -12px rgba(15, 20, 16, 0.18);
  cursor: pointer;
  transform-origin: calc(100% - 28px) 100%;
  animation: moss-teaser-in 0.55s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both;
  transition: translate 0.2s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.2s ease;
}
[data-moss-teaser]:hover {
  translate: 0 -1px;
  box-shadow:
    inset 0 0 0 1px rgba(15, 20, 16, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.9),
    0 1px 2px rgba(15, 20, 16, 0.05),
    0 8px 18px -4px rgba(15, 20, 16, 0.1),
    0 28px 52px -12px rgba(15, 20, 16, 0.22);
}
[data-moss-teaser]:active { translate: 0 0; }
/* Tail toward the orb: the lower half of a rotated square, opaque so the
   card's drop shadow cannot show through it, and 1px up so it covers the
   card's hairline where they join. */
[data-moss-teaser]::after {
  content: "";
  position: absolute;
  right: 22px;
  bottom: -5px;
  width: 12px;
  height: 12px;
  background: #fff;
  border-radius: 0 0 3px 0;
  box-shadow: inset -1px -1px 0 rgba(15, 20, 16, 0.07);
  clip-path: polygon(100% 0, 100% 100%, 0 100%);
  transform: rotate(45deg);
}
:host([data-side="left"]) [data-moss-teaser] { transform-origin: 28px 100%; }
:host([data-side="left"]) [data-moss-teaser]::after { right: auto; left: 22px; }
:host([data-side="center"]) [data-moss-teaser] { transform-origin: 50% 100%; }
:host([data-side="center"]) [data-moss-teaser]::after { right: auto; left: calc(50% - 6px); }
[data-moss-theme="dark"] [data-moss-teaser] {
  background: rgba(24, 26, 30, 0.95);
  color: rgba(255, 255, 255, 0.92);
  box-shadow:
    inset 0 0 0 1px rgba(255, 255, 255, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 1px 2px rgba(0, 0, 0, 0.3),
    0 8px 18px -4px rgba(0, 0, 0, 0.35),
    0 24px 48px -12px rgba(0, 0, 0, 0.5);
}
[data-moss-theme="dark"] [data-moss-teaser]::after { background: #181a1e; box-shadow: inset -1px -1px 0 rgba(255, 255, 255, 0.08); }
[data-moss-teaser-text] {
  flex: 1;
  min-width: 0;
  padding: 4px 0;
  text-align: left;
  font-family: ${TEASER_FONT_FAMILY};
  font-size: 15px;
  line-height: 1.3;
  letter-spacing: -0.01em;
  text-wrap: pretty;
  overflow-wrap: anywhere;
}
[data-moss-teaser-text]:focus-visible {
  outline: 2px solid var(--moss-teaser-accent, currentColor);
  outline-offset: 3px;
  border-radius: 4px;
}
[data-moss-teaser-close] {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  margin-top: 1px;
  border-radius: 9999px;
  color: rgba(15, 20, 16, 0.36);
  transition: color 0.15s, background-color 0.15s;
}
[data-moss-teaser-close] svg { width: 12px; height: 12px; }
[data-moss-teaser-close]:hover { background: rgba(15, 20, 16, 0.06); color: rgba(15, 20, 16, 0.78); }
[data-moss-teaser-close]:focus-visible { outline: 2px solid var(--moss-teaser-accent, currentColor); outline-offset: 1px; }
[data-moss-theme="dark"] [data-moss-teaser-close] { color: rgba(255, 255, 255, 0.42); }
[data-moss-theme="dark"] [data-moss-teaser-close]:hover { background: rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.85); }

/* Chat panel: sits above the attribution in the bubble column while the chat
   toggle is open. */
[data-moss-transcript] {
  display: flex;
  flex-direction: column;
  width: 340px;
  /* 8px clear of the far edge whatever the offset (32px at the default 24px). */
  max-width: calc(100vw - 8px - var(--moss-offset-x, 24px));
  margin: 0 4px 6px 0;
  padding: 12px;
  border-radius: 16px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.95);
  color: #000;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}
:host([data-side="left"]) [data-moss-transcript] { margin: 0 0 6px 4px; }
:host([data-side="center"]) [data-moss-transcript] { margin: 0 0 6px; }
[data-moss-theme="dark"] [data-moss-transcript] {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(27, 29, 33, 0.95);
  color: #fff;
}
[data-moss-prompts] {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}
[data-moss-prompts]:empty { display: none; }
[data-moss-prompt] {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 9999px;
  padding: 4px 10px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.7);
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-prompt]:hover { background: rgba(0, 0, 0, 0.06); }
[data-moss-prompt]:disabled { opacity: 0.5; cursor: default; background: transparent; }
[data-moss-theme="dark"] [data-moss-prompt] { border-color: rgba(255, 255, 255, 0.1); color: rgba(255, 255, 255, 0.8); }
[data-moss-theme="dark"] [data-moss-prompt]:hover { background: rgba(255, 255, 255, 0.1); }
[data-moss-theme="dark"] [data-moss-prompt]:disabled { background: transparent; }
[data-moss-hint] {
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 16px;
  font-style: italic;
  color: rgba(0, 0, 0, 0.4);
}
[data-moss-theme="dark"] [data-moss-hint] { color: rgba(255, 255, 255, 0.4); }
[data-moss-turns] {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 8px;
  scroll-behavior: smooth;
}
[data-moss-turn] {
  max-width: 85%;
  border-radius: 12px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.375;
  word-break: break-word;
}
[data-moss-turn="user"] { align-self: flex-end; background: rgba(0, 0, 0, 0.06); }
[data-moss-turn="assistant"] { align-self: flex-start; background: rgba(0, 0, 0, 0.03); }
[data-moss-turn][data-pending] { opacity: 0.6; }
[data-moss-theme="dark"] [data-moss-turn="user"] { background: rgba(255, 255, 255, 0.1); }
[data-moss-theme="dark"] [data-moss-turn="assistant"] { background: rgba(255, 255, 255, 0.04); }
[data-moss-chat-form] {
  display: flex;
  align-items: center;
  gap: 6px;
}
[data-moss-chat-input] {
  flex: 1;
  min-width: 0;
  height: 32px;
  padding: 0 12px;
  border-radius: 9999px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  color: rgba(0, 0, 0, 0.8);
  /* text-base below the sm breakpoint: mobile Safari zooms the page on focus
     under 16px. */
  font-size: 16px;
  line-height: 24px;
  outline: none;
}
@media (min-width: 640px) {
  [data-moss-chat-input] { font-size: 12px; line-height: 16px; }
}
[data-moss-chat-input]::placeholder { color: rgba(0, 0, 0, 0.3); }
[data-moss-chat-input]:focus-visible { box-shadow: 0 0 0 1px #fff, 0 0 0 3px rgba(0, 0, 0, 0.3); }
[data-moss-theme="dark"] [data-moss-chat-input] {
  border-color: rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
}
[data-moss-theme="dark"] [data-moss-chat-input]::placeholder { color: rgba(255, 255, 255, 0.3); }
[data-moss-theme="dark"] [data-moss-chat-input]:focus-visible { box-shadow: 0 0 0 1px #fff, 0 0 0 3px rgba(255, 255, 255, 0.4); }
[data-moss-chat-send] {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  color: rgba(0, 0, 0, 0.7);
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-chat-send]:hover { background: rgba(0, 0, 0, 0.06); }
[data-moss-chat-send]:disabled { color: inherit; background: transparent; opacity: 0.3; cursor: not-allowed; }
[data-moss-theme="dark"] [data-moss-chat-send] { color: rgba(255, 255, 255, 0.8); }
[data-moss-theme="dark"] [data-moss-chat-send]:hover { background: rgba(255, 255, 255, 0.1); }
[data-moss-theme="dark"] [data-moss-chat-send]:disabled { color: inherit; background: transparent; }

/* Handoff toast: small card at top-right, no scrim. Anchored away from the
   bubble column (bottom-right), which is also visible during a handoff. */
[data-moss-handoff-toast] {
  position: fixed;
  top: 16px;
  right: 24px;
  max-width: min(340px, calc(100vw - 48px));
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: #111;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  pointer-events: auto;
  z-index: 2147483001;
}

/* Mic-error card: fixed top-center banner. */
[data-moss-mic-error] {
  position: fixed;
  top: 16px;
  left: 50%;
  transform: translateX(-50%);
  max-width: min(360px, calc(100vw - 32px));
  background: #fff;
  border-radius: 12px;
  padding: 12px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  font-size: 14px;
  color: #111;
  pointer-events: auto;
  z-index: 2147483004;
}
[data-moss-mic-title] { font-weight: 600; margin: 0 0 4px; }
[data-moss-mic-body] { margin: 0 0 8px; color: rgba(0,0,0,0.7); }
[data-moss-mic-retry] {
  border: 1px solid rgba(0,0,0,0.15);
  border-radius: 8px;
  background: #fff;
  padding: 6px 12px;
  font-size: 13px;
  cursor: pointer;
}

/* Booking: the React BookingForm card (always light, like the intake dialog). */
[data-moss-booking-backdrop] {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  pointer-events: auto;
  z-index: 2147483002;
}
[data-moss-booking-modal] {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  width: min(600px, calc(100vw - 40px));
  max-height: calc(100dvh - 32px);
  border-radius: 16px;
  background: #fff;
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  pointer-events: auto;
  z-index: 2147483003;
}
[data-moss-booking-modal][data-moss-booking-step="1"] { max-height: min(560px, calc(100dvh - 64px)); }
[data-moss-booking-header] {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
[data-moss-booking-heading] { display: flex; align-items: center; gap: 8px; }
[data-moss-booking-title] {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(0, 0, 0, 0.4);
}
[data-moss-booking-back] {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  color: rgba(0, 0, 0, 0.4);
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-booking-close] {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  color: rgba(0, 0, 0, 0.3);
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-booking-back]:hover, [data-moss-booking-close]:hover { background: rgba(0, 0, 0, 0.06); }
[data-moss-booking-body] {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.14) transparent;
}
[data-moss-booking-body]::-webkit-scrollbar { width: 6px; }
[data-moss-booking-body]::-webkit-scrollbar-track { background: transparent; }
[data-moss-booking-body]::-webkit-scrollbar-thumb { border-radius: 9999px; background: rgba(0, 0, 0, 0.1); }
[data-moss-booking-body] p { margin: 0; }
[data-moss-slots-loading], [data-moss-fields-loading] { display: flex; justify-content: center; padding: 40px 0; }
[data-moss-fields-loading] { padding: 32px 0; }
[data-moss-slots-loading] svg, [data-moss-fields-loading] svg { color: rgba(0, 0, 0, 0.4); animation: moss-spin 1s linear infinite; }
[data-moss-slots-error] {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 32px 0;
  text-align: center;
}
[data-moss-slots-error] svg { color: #f87171; }
[data-moss-slots-error] p { font-size: 12px; line-height: 16px; color: #ef4444; }
[data-moss-slots-retry] {
  margin-top: 4px;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.7);
}
[data-moss-slots-retry]:hover { text-decoration: underline; }
[data-moss-slots-empty], [data-moss-booking-unavailable] {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 48px 24px;
  text-align: center;
}
[data-moss-slots-empty] svg, [data-moss-booking-unavailable] svg { color: rgba(0, 0, 0, 0.2); }
[data-moss-slots-empty] p:first-of-type, [data-moss-booking-unavailable] p:first-of-type {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.4);
}
[data-moss-slots-empty] p:last-of-type, [data-moss-booking-unavailable] p:last-of-type {
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.25);
}
[data-moss-slot-days] { display: flex; flex-direction: column; gap: 20px; }
[data-moss-slot-day] { display: flex; flex-direction: column; gap: 10px; }
[data-moss-slot-day-header] { display: flex; align-items: center; gap: 12px; padding: 0 2px; }
[data-moss-slot-day-header] p {
  flex-shrink: 0;
  font-size: 11px;
  line-height: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(0, 0, 0, 0.35);
}
[data-moss-slot-day-rule] { height: 1px; flex: 1; background: rgba(0, 0, 0, 0.06); }
[data-moss-slot-grid] { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 8px; }
[data-moss-slot] {
  border-radius: 12px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: #fff;
  padding: 8px 12px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.65);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  transition: all 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-slot]:hover {
  border-color: rgba(0, 0, 0, 0.3);
  background: rgba(0, 0, 0, 0.04);
  color: rgba(0, 0, 0, 0.9);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px -1px rgba(0, 0, 0, 0.1);
  transform: scale(1.03);
}
[data-moss-slot]:active { transform: scale(0.97); }
[data-moss-selected-slot] {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.03);
  padding: 8px 12px;
}
[data-moss-selected-slot] svg { flex-shrink: 0; color: rgba(0, 0, 0, 0.45); }
[data-moss-selected-slot] p { font-size: 12px; line-height: 16px; font-weight: 500; color: rgba(0, 0, 0, 0.65); }
[data-moss-booking-form] { display: flex; flex-direction: column; gap: 12px; }
[data-moss-booking-field] { display: flex; flex-direction: column; gap: 4px; }
[data-moss-booking-field] label {
  font-size: 10px;
  line-height: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: rgba(0, 0, 0, 0.3);
}
[data-moss-booking-required] { color: #f87171; margin-left: 4px; }
[data-moss-booking-field] input, [data-moss-booking-field] textarea {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  padding: 8px 12px;
  font: inherit;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.7);
  outline: none;
  transition: border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-booking-field] textarea { resize: none; }
[data-moss-booking-field] input::placeholder, [data-moss-booking-field] textarea::placeholder { color: rgba(0, 0, 0, 0.25); }
[data-moss-booking-field] input:focus, [data-moss-booking-field] textarea:focus {
  border-color: rgba(0, 0, 0, 0.3);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}
[data-moss-booking-error], [data-moss-fields-error] {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid rgba(254, 202, 202, 0.6);
  background: #fef2f2;
  padding: 8px 12px;
}
[data-moss-booking-error] svg, [data-moss-fields-error] svg { flex-shrink: 0; margin-top: 2px; color: #f87171; }
[data-moss-booking-error] p, [data-moss-fields-error] p { font-size: 12px; line-height: 16px; color: #dc2626; }
[data-moss-booking-submit] {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 9999px;
  padding: 10px 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #fff;
  background: #000;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-booking-submit]:hover { background: rgba(0, 0, 0, 0.85); }
[data-moss-booking-submit]:disabled { background: rgba(0, 0, 0, 0.4); cursor: not-allowed; }
[data-moss-booking-submit] svg { color: rgba(255, 255, 255, 0.7); animation: moss-spin 1s linear infinite; }
[data-moss-booking-success] {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 40px 24px;
  text-align: center;
  outline: none;
}
[data-moss-booking-success] svg { color: #10b981; }
[data-moss-booking-success-text] { display: flex; flex-direction: column; gap: 4px; }
[data-moss-booking-success-text] p:first-of-type { font-size: 14px; line-height: 20px; font-weight: 600; color: rgba(0, 0, 0, 0.7); }
[data-moss-booking-success-text] p:last-of-type { font-size: 12px; line-height: 16px; color: rgba(0, 0, 0, 0.4); }
[data-moss-booking-success-note] { font-size: 11px; line-height: 15px; color: rgba(0, 0, 0, 0.3); }
[data-moss-booking-footer] {
  flex-shrink: 0;
  padding: 8px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}
[data-moss-booking-footer] p {
  margin: 0;
  font-size: 9px;
  line-height: 13.5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  text-align: center;
  color: rgba(0, 0, 0, 0.25);
}
[data-moss-booking-footer] span { color: rgba(0, 0, 0, 0.4); }

/* Intake: the React IntakeForm card (always light; the React form has no dark
   variant). Own attribute namespace since booking and intake are driven
   independently. */
[data-moss-intake-backdrop] {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(3px);
  -webkit-backdrop-filter: blur(3px);
  pointer-events: auto;
  z-index: 2147483002;
}
[data-moss-intake-modal] {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  width: min(480px, calc(100vw - 40px));
  max-height: calc(100dvh - 32px);
  border-radius: 16px;
  background: #fff;
  color: #000;
  border: 1px solid rgba(0, 0, 0, 0.05);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  pointer-events: auto;
  z-index: 2147483003;
}
[data-moss-intake-header] {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 12px 20px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}
[data-moss-intake-title] {
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(0, 0, 0, 0.4);
}
[data-moss-intake-close] {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 9999px;
  color: rgba(0, 0, 0, 0.3);
  transition: color 0.15s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-intake-close]:hover { background: rgba(0, 0, 0, 0.06); }
[data-moss-intake-body] {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.14) transparent;
}
[data-moss-intake-body]::-webkit-scrollbar { width: 6px; }
[data-moss-intake-body]::-webkit-scrollbar-track { background: transparent; }
[data-moss-intake-body]::-webkit-scrollbar-thumb { border-radius: 9999px; background: rgba(0, 0, 0, 0.1); }
[data-moss-intake-form] { display: flex; flex-direction: column; gap: 12px; }
[data-moss-intake-field] { display: flex; flex-direction: column; gap: 4px; }
[data-moss-intake-field] label {
  font-size: 10px;
  line-height: 15px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
  color: rgba(0, 0, 0, 0.3);
}
[data-moss-intake-required] { color: #f87171; margin-left: 4px; }
[data-moss-intake-field] input {
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
  padding: 8px 12px;
  font-size: 14px;
  line-height: 20px;
  color: rgba(0, 0, 0, 0.7);
  outline: none;
  transition: border-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-intake-field] input::placeholder { color: rgba(0, 0, 0, 0.25); }
[data-moss-intake-field] input:focus {
  border-color: rgba(0, 0, 0, 0.3);
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.05);
}
[data-moss-intake-error] {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid rgba(254, 202, 202, 0.6);
  background: #fef2f2;
  padding: 8px 12px;
}
[data-moss-intake-error] svg { flex-shrink: 0; margin-top: 2px; color: #f87171; }
[data-moss-intake-error] p { margin: 0; font-size: 12px; line-height: 16px; color: #dc2626; }
[data-moss-intake-submit] {
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 9999px;
  padding: 10px 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #fff;
  background: #000;
  transition: background-color 0.15s cubic-bezier(0.4, 0, 0.2, 1);
}
[data-moss-intake-submit]:hover { background: rgba(0, 0, 0, 0.85); }
[data-moss-intake-submit]:disabled { background: rgba(0, 0, 0, 0.4); cursor: not-allowed; }
[data-moss-intake-submit] svg { color: rgba(255, 255, 255, 0.7); animation: moss-spin 1s linear infinite; }
[data-moss-intake-footer] {
  flex-shrink: 0;
  padding: 8px 20px;
  border-top: 1px solid rgba(0, 0, 0, 0.04);
}
[data-moss-intake-footer] p {
  margin: 0;
  font-size: 9px;
  line-height: 13.5px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  text-align: center;
  color: rgba(0, 0, 0, 0.25);
}
[data-moss-intake-footer] span { color: rgba(0, 0, 0, 0.4); }

@keyframes moss-aura-pulse {
  0%, 100% { transform: scale(0.9); opacity: 0.7; }
  50% { transform: scale(1.05); opacity: 1; }
}
@keyframes moss-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
@keyframes moss-spin {
  to { transform: rotate(360deg); }
}
@keyframes moss-teaser-in {
  from { opacity: 0; transform: translateY(8px) scale(0.96); }
  to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .moss-bubble, [data-moss-bubble], [data-moss-avatar] { transition: none !important; }
  [data-moss-teaser] { animation: none !important; transition: none !important; translate: none !important; }
  [data-moss-connecting-ring], [data-moss-intake-submit] svg, [data-moss-booking-submit] svg, [data-moss-slots-loading] svg, [data-moss-fields-loading] svg { animation: none !important; }
  [data-moss-slot] { transition: none !important; }
  [data-moss-turns] { scroll-behavior: auto; }
  canvas { animation: none !important; }
}
`;
